import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from "mobx";

import type { Uuid } from "@/domains/global/identifiers";
import type { JsonObject } from "@/domains/common/types";
import { CacheKey } from "@/modules/cache/constants";
import { LEGACY_GLOBAL_CACHE_INSTANCE } from "@/modules/cache/globalCache";
import { audioRecordingModule } from "@/modules/audio-recording";
import { getVoiceModeStartRejection } from "@/modules/audio-recording/voice-start-admission";
import { electronModule } from "@/modules/electron";
import { TranscriptionStatus } from "@/store/audio/recording";
import type { AppSubStoreArgs } from "@/store/types";
import { AppSubStore } from "@/store/types";
import { AppStoreAudioRecordingStore } from "@/store/audio/recording/audio-recording.store";
import { AppStoreAudioSuggestionStore } from "@/store/audio/suggestion";
import { AppStoreAudioTranscriptionStore } from "@/store/audio/transcription";
import {
  AppStoreAudioTransformingStore,
  AudioRecordingTransformingState,
} from "@/store/audio/transforming";
import { AppStoreAudioUiStore } from "@/store/audio/ui";
import { AppStoreAudioUploadingStore, AudioRecordingUploadingState } from "@/store/audio/uploading";
import { AppStoreAudioPipelineStore } from "@/store/audio/pipeline/audio-pipeline.store";
import { buildAudioRecordingArtifactDiagnostics } from "@/store/audio/audio-recording-artifact-diagnostics";
import {
  type IAudioStore,
  type TrashRecordingContext,
  TrashRecordingReason,
} from "@/store/audio/audio.types";
import { AudioRecorderLockHandle } from "@/modules/audio-recording/audio-recorder-lock";
import { logger } from "@/modules/logger";
import { AudioRecorderLockingError } from "@/domains/errors/kinds/contention";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import {
  AudioRecordingObservable,
  AudioRecordingModelData,
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingStrategy,
  AudioRecordingUploadStatus,
  IndexedAudioRecordingSyncUpdateValue,
  getMeetingHandoffSuccessorAudioRecordingId,
  getAudioRecordingStrategy,
  isPipelineDeferredToMeetingHandoffSuccessor,
} from "@/store/audio-recordings";
import { TrackedEvent, type VoiceSessionEventParams } from "@/domains/metrics/tracked-event";
import { trackEvent } from "@/domains/metrics";
import { CustomEventName, dispatchCustomEvent } from "@/modules/custom-event";
import { clientEnvModule } from "@/modules/client-env";
import {
  RecordingWindow,
  recordingWindowForDetectedMeetingWindowId,
} from "@/store/audio/recording/recording-window";
import { uuidModule } from "@/modules/uuid";
import { deleteNoteSource } from "@/actions/deleteNoteSource";
import { DeleteSourceOperation } from "@/store/sync/operations/sources/DeleteSourceOperation";
import { STEP_IDS } from "@/store/getting-started";
import {
  AudioRecordingStrategyDriver,
  DesktopArtifactRecordingStrategyDriver,
  UploadableAudioRecordingArtifactStatus,
  WebArtifactRecordingStrategyDriver,
} from "@/store/audio/recording/strategies";
import { getDesktopRecordingStartFailureError } from "@/store/audio/recording/media-controller/desktop-controller-errors";

const MEETING_HANDOFF_RESTARTING_STATUS_MAX_MS = 2_000;

type RecordingLockAction = {
  audioRecordingId: Uuid;
  action: () => Promise<void>;
  toastMessage: string;
};

type ClientResetAudioRecoveryContext = {
  previousClientGroupIds: Set<Uuid>;
  currentClientGroupId: Uuid;
  audioRecordingIds: Set<Uuid>;
};

type ClientResetAudioRecoveryPlan = {
  context: ClientResetAudioRecoveryContext | null;
  shouldClearMarker: boolean;
};

type CreateAudioRecordingArgs = {
  audioRecordingId?: Uuid;
  expandVoicePopover?: boolean;
  associatedNoteId?: Uuid | null;
  /** See IAudioStore.createAndStartAudioRecording. */
  meetingEventWindowId?: string;
  /** Which window the Recall SDK records; defaults to whole-desktop. */
  recordingWindow?: RecordingWindow;
  /** See IAudioStore.createAndStartAudioRecording. */
  preferDetectedMeetingWindow?: boolean;
  calendarEventId?: string;
  config?: {
    showOnboardingTooltip: boolean;
    forceWebMediaController?: boolean;
  };
};

type ResolveNewRecordingStrategyArgs = {
  forceWebMediaController?: boolean;
};

export type DetectedMeetingHandoffTransition = {
  previousAudioRecordingId: Uuid;
  successorAudioRecordingId: Uuid;
  associatedNoteId: Uuid;
};

export class AppStoreAudioStore extends AppSubStore implements IAudioStore {
  ui: AppStoreAudioUiStore;
  recording: AppStoreAudioRecordingStore;
  transcription: AppStoreAudioTranscriptionStore;
  uploading: AppStoreAudioUploadingStore;
  transforming: AppStoreAudioTransformingStore;
  pipeline: AppStoreAudioPipelineStore;
  suggestion: AppStoreAudioSuggestionStore;
  /**
   * UI-only marker for the short window where detected-meeting handoff has
   * switched the active recording id to the successor but we keep the previous
   * transcript panel visible with a restart status message.
   */
  detectedMeetingHandoffTransition: DetectedMeetingHandoffTransition | null = null;

  private detectedMeetingHandoffTransitionTimeout: ReturnType<typeof setTimeout> | null = null;
  private activeRecordingLockHandle: AudioRecorderLockHandle | null = null;
  private activeRecordingIdValue: Uuid | null = null;
  private activeRecordingNoteIdValue: Uuid | null = null;
  private deactivateOrphanedActiveRecordingReaction: IReactionDisposer;
  private readonly webArtifactStrategyDriver: WebArtifactRecordingStrategyDriver;
  private readonly desktopArtifactStrategyDriver: DesktopArtifactRecordingStrategyDriver;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.ui = new AppStoreAudioUiStore(injectedDeps);
    this.ui.initialize();
    this.webArtifactStrategyDriver = new WebArtifactRecordingStrategyDriver({
      webAudioArtifacts: this.store.dexie.repositories.webAudioArtifacts,
    });
    this.desktopArtifactStrategyDriver = new DesktopArtifactRecordingStrategyDriver();

    this.uploading = new AppStoreAudioUploadingStore({
      ...injectedDeps,
      config: {
        onUploadComplete: () => {},
      },
    });

    this.recording = new AppStoreAudioRecordingStore({
      ...injectedDeps,
      webArtifactStrategyDriver: this.webArtifactStrategyDriver,
      desktopArtifactStrategyDriver: this.desktopArtifactStrategyDriver,
      uiStore: this.ui,
      config: {
        onRecordingCompleted: ({ audioRecordingId }) => {
          this.pipeline.onRecordingCompleted({ audioRecordingId });
        },
      },
    });

    this.transcription = new AppStoreAudioTranscriptionStore(injectedDeps);

    this.transforming = new AppStoreAudioTransformingStore({
      ...injectedDeps,
      transcriptionStore: this.transcription,
    });

    this.pipeline = new AppStoreAudioPipelineStore(injectedDeps);

    this.suggestion = new AppStoreAudioSuggestionStore({
      ...injectedDeps,
      uploadingStore: this.uploading,
      transformingStore: this.transforming,
    });

    makeObservable<
      this,
      | "activeRecordingLockHandle"
      | "withRecordingTabLock"
      | "obtainAudioRecordingLock"
      | "releaseAudioRecordingLock"
      | "setActiveAudioRecording"
      | "clearActiveAudioRecording"
      | "activeRecordingIdValue"
      | "activeRecordingNoteIdValue"
      | "hasLockInCurrentTab"
      | "maybeObtainAudioRecordingLock"
      | "cleanupActiveDesktopRecordingAfterRecoveryFailure"
      | "deactivateOrphanedActiveRecordingReaction"
      | "shouldBlockRecordingStart"
      | "buildVoiceSessionEventParams"
      | "promptReplaceActiveRecording"
      | "webArtifactStrategyDriver"
      | "desktopArtifactStrategyDriver"
      | "reconcilePersistedAudioState"
      | "getClientResetAudioRecoveryPlan"
      | "getPersistedAudioRecordsForReconciliation"
      | "recoverClientResetAudioRecordingIfNeeded"
      | "reconcileRecordingState"
      | "hasTerminalProcessingStatus"
      | "hasTerminalUploadStatus"
      | "hasUploadStatusThatBlocksProcessing"
      | "startDeferredMeetingHandoffSegmentUploads"
      | "detectedMeetingHandoffTransitionTimeout"
      | "beginDetectedMeetingHandoffTransition"
      | "clearDetectedMeetingHandoffTransition"
      | "clearDetectedMeetingHandoffTransitionTimer"
      | "buildTrashRecordingDiagnostics"
      | "deleteSourceForRecording"
      | "resolveNewRecordingStrategy"
      | "resolveDetectedMeetingWindowArgsForStart"
      | "getStrategyDriver"
      | "getUploadableArtifactStatus"
    >(this, {
      recording: observable,
      transcription: observable,
      uploading: observable,
      transforming: observable,
      pipeline: observable,
      suggestion: observable,
      ui: observable,
      detectedMeetingHandoffTransition: observable.ref,
      detectedMeetingHandoffTransitionTimeout: false,
      activeRecordingLockHandle: observable,
      activeRecordingId: computed,
      activeRecordingNoteId: computed,
      activeRecording: computed,
      hasActiveRecording: computed,
      createAndStartAudioRecording: action,
      replaceActiveRecordingWithDetectedMeetingWindow: action,
      prepareRecording: action,
      pauseRecording: action,
      resumeRecording: action,
      finishRecording: action,
      trashRecording: action,
      startSourceUpload: action,
      processRecording: action,
      initialize: action,
      withRecordingTabLock: action,
      obtainAudioRecordingLock: action,
      releaseAudioRecordingLock: action,
      setActiveAudioRecording: action,
      clearActiveAudioRecording: action,
      getUploadStateForRecording: false,
      getTransformStateForRecording: false,
      _activateRecording: action,
      _deactivateRecording: action,
      dispose: action,
      resetState: action,
      rehydrateAndObtainAudioRecordingLockIfNecessary: action,
      maybeObtainAudioRecordingLock: action,
      cleanupActiveDesktopRecordingAfterRecoveryFailure: action,
      activeRecordingIdValue: observable,
      activeRecordingNoteIdValue: observable,
      hasLockInCurrentTab: computed,
      deactivateOrphanedActiveRecordingReaction: observable.ref,
      ensureActiveRecordingStateIsValid: action,
      shouldBlockRecordingStart: action,
      buildVoiceSessionEventParams: false,
      promptReplaceActiveRecording: false,
      webArtifactStrategyDriver: false,
      desktopArtifactStrategyDriver: false,
      reconcilePersistedAudioState: false,
      getClientResetAudioRecoveryPlan: false,
      getPersistedAudioRecordsForReconciliation: false,
      recoverClientResetAudioRecordingIfNeeded: false,
      reconcileRecordingState: false,
      hasTerminalProcessingStatus: false,
      hasTerminalUploadStatus: false,
      hasUploadStatusThatBlocksProcessing: false,
      startDeferredMeetingHandoffSegmentUploads: false,
      beginDetectedMeetingHandoffTransition: action,
      clearDetectedMeetingHandoffTransition: action,
      clearDetectedMeetingHandoffTransitionOnTranscript: action,
      clearDetectedMeetingHandoffTransitionTimer: false,
      buildTrashRecordingDiagnostics: false,
      deleteSourceForRecording: false,
      resolveNewRecordingStrategy: false,
      resolveDetectedMeetingWindowArgsForStart: false,
      getStrategyDriver: false,
      getUploadableArtifactStatus: false,
    });

    /**
     * If our audio recording becomes orphaned, then we automatically trash it.
     */
    this.deactivateOrphanedActiveRecordingReaction = reaction(
      () => {
        if (!this.hasLockInCurrentTab) {
          return { shouldDeactivate: false };
        }

        const activeRecordingId = this.activeRecordingIdValue;
        if (!activeRecordingId) {
          return { shouldDeactivate: false };
        }

        const recording = this.store.audioRecordings.get(activeRecordingId);
        if (!recording) {
          return { shouldDeactivate: false };
        }

        /**
         * If the audio recording has no associatedNoteId, then that is alright.
         * (Note that this kind of state is currently only used for internal/testing purposes)
         */
        const noteId = recording.associatedNoteId;
        if (!noteId) {
          return { shouldDeactivate: false };
        }

        const note = this.store.notes.getNoteObservableById({ noteId });
        const doesNotExist = this.store.notes.doesNotExist(noteId);

        const isTrashedOrDeleted = note ? note.isTrashed || note.isDeleted : false;

        return {
          audioRecordingId: activeRecordingId,
          shouldDeactivate: isTrashedOrDeleted || doesNotExist,
        };
      },
      async ({ audioRecordingId, shouldDeactivate }, _prev) => {
        if (!audioRecordingId) return;
        if (!shouldDeactivate) return;

        try {
          await this.trashRecording({
            audioRecordingId,
            context: { reason: TrashRecordingReason.NoteRemoved },
          });
        } catch (unknownErr) {
          logger.warn({
            message:
              "[AppStoreAudioStore] failed to deactivate orphaned active recording after note removal",
            info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
          });
        }
      }
    );
  }

  get activeRecordingId(): Uuid | null {
    if (!this.hasLockInCurrentTab) return null;

    return this.activeRecordingIdValue ?? null;
  }

  get activeRecording(): AudioRecordingObservable | null {
    if (!this.hasLockInCurrentTab) return null;

    const activeId = this.activeRecordingIdValue;
    if (!activeId) return null;

    return this.store.audioRecordings.get(activeId) ?? null;
  }

  get activeRecordingNoteId(): Uuid | null {
    if (this.activeRecordingNoteIdValue) return this.activeRecordingNoteIdValue;
    const recording = this.activeRecording;
    if (!recording) return null;

    return (recording.associatedNoteId as Uuid | undefined) ?? null;
  }

  get hasActiveRecording(): boolean {
    return this.activeRecordingId !== null;
  }

  private promptReplaceActiveRecording({
    activeRecordingId,
    requestedAudioRecordingId,
    associatedNoteId,
    meetingEventWindowId,
    recordingWindow,
    preferDetectedMeetingWindow,
    calendarEventId,
    expandVoicePopover,
    config,
  }: {
    activeRecordingId: Uuid;
    requestedAudioRecordingId: Uuid | null;
    associatedNoteId?: Uuid | null;
    meetingEventWindowId?: string;
    recordingWindow?: RecordingWindow;
    preferDetectedMeetingWindow?: boolean;
    calendarEventId?: string | null;
    expandVoicePopover: boolean;
    config?: CreateAudioRecordingArgs["config"];
  }): void {
    this.ui.promptToReplaceActiveRecording({
      onConfirm: async () => {
        await this.finishRecording({ audioRecordingId: activeRecordingId });

        await this.createAndStartAudioRecording({
          audioRecordingId: requestedAudioRecordingId ?? undefined,
          associatedNoteId,
          meetingEventWindowId,
          recordingWindow,
          preferDetectedMeetingWindow,
          calendarEventId: calendarEventId ?? undefined,
          expandVoicePopover,
          config,
        });
      },
    });
  }

  async createAndStartAudioRecording({
    audioRecordingId: providedAudioRecordingId,
    expandVoicePopover = true,
    associatedNoteId,
    meetingEventWindowId,
    recordingWindow,
    preferDetectedMeetingWindow = true,
    calendarEventId,
    config,
  }: CreateAudioRecordingArgs): Promise<{ audioRecordingId: Uuid } | null> {
    if (clientEnvModule.isMobileBrowser()) {
      toastModule.triggerToast({
        content: "Voice recording is not available on mobile browsers",
        toastId: "voice-recording-not-available-on-mobile-browsers",
      });
      return null;
    }

    if (!this.store.systemPermissions.hasRequiredVoicePermissions) {
      this.store.systemPermissions.ui.openVoicePermissionsModal();
      return null;
    }

    const detectedMeetingWindowArgs = this.resolveDetectedMeetingWindowArgsForStart({
      meetingEventWindowId,
      recordingWindow,
      preferDetectedMeetingWindow,
      forceWebMediaController: config?.forceWebMediaController ?? false,
    });
    const resolvedMeetingEventWindowId = detectedMeetingWindowArgs.meetingEventWindowId;
    const resolvedRecordingWindow = detectedMeetingWindowArgs.recordingWindow;

    if (!resolvedMeetingEventWindowId) {
      // Starts not driven by a meeting notification (the notification flow
      // pushes its own "auto-start-voice" debug event).
      this.store.electron.pushRecallDebugEvent({}, "in-app-voice-recording");
    }
    const requestedAudioRecordingId = providedAudioRecordingId ?? null;
    const activeRecordingId = this.activeRecordingIdValue;

    // If this tab already owns the lock and a different recording is active,
    // prompt before claiming prepared assets to avoid Recall SDK races.
    if (
      this.hasLockInCurrentTab &&
      activeRecordingId &&
      requestedAudioRecordingId !== activeRecordingId
    ) {
      this.promptReplaceActiveRecording({
        activeRecordingId,
        requestedAudioRecordingId,
        associatedNoteId,
        meetingEventWindowId: resolvedMeetingEventWindowId,
        recordingWindow: resolvedRecordingWindow,
        preferDetectedMeetingWindow,
        calendarEventId,
        expandVoicePopover,
        config,
      });
      return null;
    }

    // Without a requested id this consumes the prewarmed slot (instant
    // start); with one it mints fresh assets bound to that id.
    const preparedRecording = this.recording.preparedRecordingPool.claim({
      audioRecordingId: providedAudioRecordingId,
    });
    const audioRecordingId = preparedRecording.audioRecordingId;
    let didStart = false;

    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        // State can change after the early check, so we re-check under the lock.
        if (this.shouldBlockRecordingStart({ audioRecordingId })) {
          const activeRecordingId = this.activeRecordingIdValue;
          if (!activeRecordingId) return;

          this.promptReplaceActiveRecording({
            activeRecordingId,
            requestedAudioRecordingId: audioRecordingId,
            associatedNoteId,
            meetingEventWindowId: resolvedMeetingEventWindowId,
            recordingWindow: resolvedRecordingWindow,
            preferDetectedMeetingWindow,
            calendarEventId,
            expandVoicePopover,
            config,
          });
          return;
        }

        const usageLimitRejection = await getVoiceModeStartRejection({ audioRecordingId });
        if (usageLimitRejection) {
          this.store.subscriptions.openCommercialUsageLimit(usageLimitRejection);
          await this.releaseAudioRecordingLock({ audioRecordingId });
          return;
        }

        if (config?.showOnboardingTooltip) {
          this.ui.setShowOnboardingTooltip(true);
        }

        logger.info({
          message: "[AppStoreAudioStore] createAndStartAudioRecording",
          info: {
            meetingEventWindowId: resolvedMeetingEventWindowId ?? null,
            recordingWindowKind: resolvedRecordingWindow?.kind ?? "whole-desktop",
            preferDetectedMeetingWindow,
          },
        });

        const recordingStrategy = this.resolveNewRecordingStrategy({
          forceWebMediaController: config?.forceWebMediaController ?? false,
        });

        const audioRecording = await this.store.dexie.repositories.audioRecordings.upsert({
          id: audioRecordingId,
          data: {
            associatedNoteId: associatedNoteId ?? null,
            recordingStrategy,
          },
        });

        await this.prepareRecording({ audioRecordingId: audioRecording.id });

        await this.setActiveAudioRecording({ audioRecordingId, associatedNoteId });

        await this.recording.start({
          preparedRecording,
          recordingWindow: resolvedRecordingWindow,
          meetingEventWindowId: resolvedMeetingEventWindowId,
          calendarEventId,
          forceWebMediaController: config?.forceWebMediaController ?? false,
        });
        didStart = true;

        if (expandVoicePopover && associatedNoteId) {
          dispatchCustomEvent(CustomEventName.ExpandVoicePopover, {
            noteId: associatedNoteId,
          });
        }

        const voiceSessionEventParams = await this.buildVoiceSessionEventParams({
          audioRecordingId: audioRecording.id as Uuid,
          associatedNoteId,
          recordingStrategy,
        });
        trackEvent(TrackedEvent.VoiceSessionStart, voiceSessionEventParams);

        if (voiceSessionEventParams.is_meeting_recording) {
          this.store.gettingStarted?.markStepComplete(STEP_IDS.RECORD_MEETING);
        }
      },
      toastMessage: "Unable to start - Mem is recording on a different tab.",
    });

    return didStart ? { audioRecordingId } : null;
  }

  /**
   * Resolves eligible desktop voice starts to the latest Recall-detected meeting.
   *
   * The renderer keeps this id from Recall meeting lifecycle events, so most
   * starts can choose the attributed meeting window without waiting on an
   * Electron request first. A caller-provided detected-meeting window still
   * wins because it represents the specific meeting event that initiated the
   * start.
   */
  private resolveDetectedMeetingWindowArgsForStart({
    meetingEventWindowId,
    recordingWindow,
    preferDetectedMeetingWindow,
    forceWebMediaController,
  }: {
    meetingEventWindowId?: string;
    recordingWindow?: RecordingWindow;
    preferDetectedMeetingWindow: boolean;
    forceWebMediaController: boolean;
  }): {
    meetingEventWindowId?: string;
    recordingWindow?: RecordingWindow;
  } {
    if (recordingWindow?.kind === "detected-meeting") {
      return { meetingEventWindowId, recordingWindow };
    }

    if (!preferDetectedMeetingWindow) {
      return { meetingEventWindowId, recordingWindow };
    }

    if (!this.store.interface.isDesktop || this.store.electron.isIntelMacDesktop) {
      return { meetingEventWindowId, recordingWindow };
    }

    if (forceWebMediaController) {
      return { meetingEventWindowId, recordingWindow };
    }

    const detectedMeetingWindow = this.store.electron.latestDetectedMeetingWindow;
    if (!detectedMeetingWindow) {
      return { meetingEventWindowId, recordingWindow };
    }

    return {
      meetingEventWindowId: detectedMeetingWindow.windowId,
      recordingWindow: recordingWindowForDetectedMeetingWindowId(detectedMeetingWindow.windowId),
    };
  }

  async replaceActiveRecordingWithDetectedMeetingWindow({
    meetingEventWindowId,
    recordingWindow,
    calendarEventId,
  }: {
    meetingEventWindowId: string;
    recordingWindow: RecordingWindow;
    calendarEventId?: string | null;
  }): Promise<{ audioRecordingId: Uuid } | null> {
    if (recordingWindow.kind !== "detected-meeting") {
      return null;
    }

    if (!this.hasLockInCurrentTab) {
      return null;
    }

    const previousAudioRecordingId = this.activeRecordingIdValue;
    const associatedNoteId = this.activeRecordingNoteId;
    if (!previousAudioRecordingId || !associatedNoteId) {
      return null;
    }

    if (
      !this.recording.canReplaceWithDetectedMeetingWindow({
        audioRecordingId: previousAudioRecordingId,
      })
    ) {
      return null;
    }

    const recordingStrategy = this.resolveNewRecordingStrategy({});
    if (recordingStrategy !== AudioRecordingStrategy.DesktopArtifact) {
      return null;
    }

    const previousSilenceSnapshot = this.recording.getCurrentSilenceSnapshot();
    // The handoff is modeled as a second Mem audio recording on the same note.
    // Backend processing remains per recording id; note-level consumers can
    // reconcile the original whole-desktop segment and the attributed meeting
    // segment through their shared associatedNoteId.
    const preparedRecording = this.recording.preparedRecordingPool.claim();
    const audioRecordingId = preparedRecording.audioRecordingId;

    logger.info({
      message: "[AppStoreAudioStore] replaceActiveRecordingWithDetectedMeetingWindow",
      info: {
        previousAudioRecordingId,
        audioRecordingId,
        associatedNoteId,
        meetingEventWindowId,
        recallWindowId: recordingWindow.recallWindowId,
      },
    });

    const audioRecording = await this.store.dexie.repositories.audioRecordings.upsert({
      id: audioRecordingId,
      data: {
        associatedNoteId,
        recordingStrategy,
      },
    });

    await this.prepareRecording({ audioRecordingId: audioRecording.id });
    this.beginDetectedMeetingHandoffTransition({
      previousAudioRecordingId,
      successorAudioRecordingId: audioRecordingId,
      associatedNoteId,
    });
    await this.setActiveAudioRecording({ audioRecordingId, associatedNoteId });
    const cleanupFailedSuccessor = async ({ logMessage }: { logMessage: string }) => {
      this.clearDetectedMeetingHandoffTransition({
        successorAudioRecordingId: audioRecordingId,
      });
      try {
        await this.trashRecording({
          audioRecordingId,
          context: { reason: TrashRecordingReason.StartRecordingFailed },
        });
      } catch (trashError) {
        logger.warn({
          message: logMessage,
          info: {
            audioRecordingId,
            previousAudioRecordingId,
            error: objectModule.safeErrorAsJson(trashError as Error),
          },
        });
      }
    };
    // Some replacement failures happen after Electron has detached the
    // predecessor, so the predecessor must be finalized instead of restored.
    const completeDetachedPredecessorAfterFailedReplacement = async () => {
      await this.clearActiveAudioRecording({ audioRecordingId });
      await this.releaseAudioRecordingLock({ audioRecordingId: null });
      const completionResult =
        await this.recording.completeSupersededRecordingAfterExternalReplacement({
          audioRecordingId: previousAudioRecordingId,
          successorAudioRecordingId: audioRecordingId,
          silenceSnapshot: previousSilenceSnapshot,
          deferPipelineToSuccessor: false,
        });

      if (completionResult.status === "empty") {
        await this.trashRecording({
          audioRecordingId: previousAudioRecordingId,
          context: { reason: TrashRecordingReason.SupersededEmptyMeetingHandoff },
        });
      } else if (completionResult.status === "completed") {
        trackEvent(
          TrackedEvent.VoiceSessionDone,
          await this.buildVoiceSessionEventParams({
            audioRecordingId: previousAudioRecordingId,
          })
        );
      }
    };

    try {
      await this.recording.start({
        preparedRecording,
        recordingWindow,
        meetingEventWindowId,
        calendarEventId: calendarEventId ?? undefined,
        replacePreviousAudioRecordingId: previousAudioRecordingId,
      });
    } catch (error) {
      const desktopStartFailureError = getDesktopRecordingStartFailureError({ error });
      if (desktopStartFailureError) {
        await completeDetachedPredecessorAfterFailedReplacement();
        await cleanupFailedSuccessor({
          logMessage:
            "[AppStoreAudioStore] failed to trash detected-meeting handoff successor after desktop start failure",
        });
        throw desktopStartFailureError;
      }

      await this.setActiveAudioRecording({
        audioRecordingId: previousAudioRecordingId,
        associatedNoteId,
      });
      await cleanupFailedSuccessor({
        logMessage:
          "[AppStoreAudioStore] failed to trash detected-meeting handoff successor after start failure",
      });
      throw error;
    }

    if (!this.recording.hasRecordingState({ audioRecordingId })) {
      if (this.recording.hasRecordingState({ audioRecordingId: previousAudioRecordingId })) {
        await this.setActiveAudioRecording({
          audioRecordingId: previousAudioRecordingId,
          associatedNoteId,
        });
      } else {
        await completeDetachedPredecessorAfterFailedReplacement();
      }
      await cleanupFailedSuccessor({
        logMessage:
          "[AppStoreAudioStore] failed to trash detected-meeting handoff successor after swallowed start failure",
      });
      return null;
    }

    const voiceSessionEventParams = await this.buildVoiceSessionEventParams({
      audioRecordingId: audioRecording.id as Uuid,
      associatedNoteId,
      recordingStrategy,
    });
    trackEvent(TrackedEvent.VoiceSessionStart, voiceSessionEventParams);

    const completionResult =
      await this.recording.completeSupersededRecordingAfterExternalReplacement({
        audioRecordingId: previousAudioRecordingId,
        successorAudioRecordingId: audioRecordingId,
        silenceSnapshot: previousSilenceSnapshot,
      });

    if (completionResult.status === "empty") {
      await this.trashRecording({
        audioRecordingId: previousAudioRecordingId,
        context: { reason: TrashRecordingReason.SupersededEmptyMeetingHandoff },
      });
    } else if (completionResult.status === "completed") {
      trackEvent(
        TrackedEvent.VoiceSessionDone,
        await this.buildVoiceSessionEventParams({
          audioRecordingId: previousAudioRecordingId,
        })
      );
    } else {
      logger.warn({
        message: "[AppStoreAudioStore] preserved superseded recording after completion failure",
        info: {
          audioRecordingId: previousAudioRecordingId,
          successorAudioRecordingId: audioRecordingId,
          errorMessage: completionResult.errorMessage,
        },
      });
    }

    return { audioRecordingId };
  }

  private beginDetectedMeetingHandoffTransition(
    transition: DetectedMeetingHandoffTransition
  ): void {
    this.clearDetectedMeetingHandoffTransitionTimer();
    this.detectedMeetingHandoffTransition = transition;
    this.detectedMeetingHandoffTransitionTimeout = setTimeout(() => {
      this.clearDetectedMeetingHandoffTransition({
        successorAudioRecordingId: transition.successorAudioRecordingId,
      });
    }, MEETING_HANDOFF_RESTARTING_STATUS_MAX_MS);
  }

  private clearDetectedMeetingHandoffTransition({
    successorAudioRecordingId,
  }: {
    successorAudioRecordingId?: Uuid;
  } = {}): void {
    if (
      successorAudioRecordingId &&
      this.detectedMeetingHandoffTransition?.successorAudioRecordingId !== successorAudioRecordingId
    ) {
      return;
    }

    this.clearDetectedMeetingHandoffTransitionTimer();
    this.detectedMeetingHandoffTransition = null;
  }

  clearDetectedMeetingHandoffTransitionOnTranscript({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): void {
    this.clearDetectedMeetingHandoffTransition({ successorAudioRecordingId: audioRecordingId });
  }

  private clearDetectedMeetingHandoffTransitionTimer(): void {
    if (!this.detectedMeetingHandoffTransitionTimeout) {
      return;
    }

    clearTimeout(this.detectedMeetingHandoffTransitionTimeout);
    this.detectedMeetingHandoffTransitionTimeout = null;
  }

  private resolveNewRecordingStrategy({
    forceWebMediaController = false,
  }: ResolveNewRecordingStrategyArgs): AudioRecordingStrategy {
    const shouldUseDesktopArtifactRecording =
      this.store.interface.isDesktop &&
      !this.store.electron.isIntelMacDesktop &&
      !forceWebMediaController;

    if (shouldUseDesktopArtifactRecording) {
      return AudioRecordingStrategy.DesktopArtifact;
    }

    return AudioRecordingStrategy.WebArtifact;
  }

  private getStrategyDriver({
    recordingStrategy,
  }: {
    recordingStrategy: AudioRecordingStrategy;
  }): AudioRecordingStrategyDriver {
    if (recordingStrategy === AudioRecordingStrategy.DesktopArtifact) {
      return this.desktopArtifactStrategyDriver;
    }

    return this.webArtifactStrategyDriver;
  }

  async getUploadableArtifactStatus({
    audioRecordingId,
    recordingStrategy,
  }: {
    audioRecordingId: Uuid;
    recordingStrategy: AudioRecordingStrategy;
  }): Promise<UploadableAudioRecordingArtifactStatus> {
    return this.getStrategyDriver({ recordingStrategy }).getUploadableArtifactStatus({
      audioRecordingId,
    });
  }

  async prepareRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    this.ui.resetRecording();
    this.recording.isPaused = false;
    this.recording.waveformSamples = null;
    this.recording.setMicrophoneState({ label: null, isDisconnected: false });
    this.recording.setVisualizer(null);
    this.recording.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Live);
    this.recording.setTranscriptionUnsupported(false);
  }

  private async buildVoiceSessionEventParams({
    audioRecordingId,
    associatedNoteId,
    recordingStrategy,
  }: {
    audioRecordingId: Uuid;
    associatedNoteId?: Uuid | null;
    recordingStrategy?: AudioRecordingStrategy;
  }): Promise<VoiceSessionEventParams> {
    const audioRecording =
      this.store.audioRecordings.get(audioRecordingId) ??
      (await this.store.audioRecordings.getAsync(audioRecordingId));
    const noteId = associatedNoteId ?? audioRecording?.associatedNoteId ?? null;
    const note = noteId ? await this.store.notes.getAsync(noteId as Uuid) : undefined;
    const hasCalendarEventSource = note?.hasCalendarEventSource ?? false;

    return {
      audio_recording_id: audioRecordingId,
      note_id: noteId,
      recording_strategy: recordingStrategy ?? audioRecording?.recordingStrategy ?? null,
      has_calendar_event_source: hasCalendarEventSource,
      is_meeting_recording: hasCalendarEventSource,
    };
  }

  async pauseRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    /**
     * Note that we don't release the lock here.
     * We only release the lock when the recording is stopped (either by finishing or by trashing).
     */
    try {
      await this.recording.pause({ audioRecordingId });
      trackEvent(
        TrackedEvent.VoiceSessionPause,
        await this.buildVoiceSessionEventParams({ audioRecordingId })
      );
    } catch (unknownErr) {
      toastModule.triggerToast({
        content: "Unable to pause recording",
        toastId: audioRecordingId,
      });

      logger.warn({
        message: "[AppStoreAudioStore] pauseRecording: failed to pause recording",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(unknownErr as Error),
        },
      });
    }
  }

  async resumeRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    if (!this.store.systemPermissions.hasRequiredVoicePermissions) {
      this.store.systemPermissions.ui.openVoicePermissionsModal();
      return;
    }

    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        if (this.shouldBlockRecordingStart({ audioRecordingId })) {
          const activeRecordingId = this.activeRecordingIdValue;
          if (!activeRecordingId) return;

          this.ui.promptToReplaceActiveRecording({
            onConfirm: async () => {
              await this.finishRecording({ audioRecordingId: activeRecordingId });

              await this.resumeRecording({ audioRecordingId });
            },
          });

          return;
        }

        /** Should be a no-op but included for completeness */
        await this.setActiveAudioRecording({ audioRecordingId });

        await this.recording.resume({ audioRecordingId });
        trackEvent(
          TrackedEvent.VoiceSessionResume,
          await this.buildVoiceSessionEventParams({ audioRecordingId })
        );
      },
      toastMessage: "Unable to resume - Mem is recording on a different tab.",
    });
  }

  private shouldBlockRecordingStart({
    audioRecordingId: requestedRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): boolean {
    if (!this.hasActiveRecording) return false;

    const activeRecordingId = this.activeRecordingIdValue;
    if (!activeRecordingId) return false;

    return requestedRecordingId !== activeRecordingId;
  }

  async finishRecording({
    audioRecordingId,
    meetingEventWindowId,
  }: {
    audioRecordingId: Uuid;
    meetingEventWindowId?: string;
  }): Promise<void> {
    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        /**
         * Note that we aren't currently using the `meetingEventWindowId` here, we just
         * include it for logging purposes.
         */
        logger.info({
          message: "[AppStoreAudioStore] finishRecording",
          info: { audioRecordingId, meetingEventWindowId: meetingEventWindowId ?? null },
        });

        await this.recording.stop({ audioRecordingId });
        await this.startDeferredMeetingHandoffSegmentUploads({
          terminalAudioRecordingId: audioRecordingId,
        });
        trackEvent(
          TrackedEvent.VoiceSessionDone,
          await this.buildVoiceSessionEventParams({ audioRecordingId })
        );

        await this.clearActiveAudioRecording({
          audioRecordingId,
        });
        await this.releaseAudioRecordingLock({ audioRecordingId });
      },
      toastMessage: "Unable to save this recording - Mem is recording on a different tab.",
    });
  }

  async trashRecording({
    audioRecordingId,
    context = { reason: TrashRecordingReason.UserDelete },
  }: {
    audioRecordingId: Uuid;
    context?: TrashRecordingContext;
  }): Promise<void> {
    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        const recording = await this.store.dexie.repositories.audioRecordings.getById({
          id: audioRecordingId,
        });
        const associatedNoteId = recording?.data.associated_note_id ?? null;
        const isActiveRecording = this.activeRecordingIdValue === audioRecordingId;
        const pipelineSnapshot = this.pipeline.getSnapshot(audioRecordingId);

        if (!recording) {
          logger.warn({
            message: "[AppStoreAudioStore] trashRecording called without a recording row",
            info: {
              audioRecordingId,
              context,
              isActiveRecording,
              pipelineStage: pipelineSnapshot?.stage ?? null,
              pipelineTerminalOutcome: pipelineSnapshot?.terminalOutcome ?? null,
            },
          });
        } else {
          try {
            logger.info({
              message: "[AppStoreAudioStore] trashing recording and deleting local artifacts",
              info: await this.buildTrashRecordingDiagnostics({
                audioRecordingId,
                modelData: recording.data,
                context,
                associatedNoteId,
                isActiveRecording,
                pipelineSnapshot,
              }),
            });
          } catch (error) {
            logger.warn({
              message: "[AppStoreAudioStore] Failed to capture trashRecording diagnostics",
              info: {
                audioRecordingId,
                context,
                error: objectModule.safeErrorAsJson(error as Error),
              },
            });
          }
        }

        await this.recording.clear({ audioRecordingId });

        if (isActiveRecording) {
          await this.clearActiveAudioRecording({
            audioRecordingId,
          });
        }

        await this.store.dexie.repositories.audioRecordings.trash({ id: audioRecordingId });
        this.uploading.clearProgress({ audioRecordingId });
        this.transforming.clearTransformState({ audioRecordingId });
        this.pipeline.clearSnapshot({ audioRecordingId });

        await this.deleteSourceForRecording({
          audioRecordingId,
          noteId: associatedNoteId,
        });
        await this.releaseAudioRecordingLock({ audioRecordingId });
      },
      toastMessage: "Unable to delete this recording - Mem is recording on a different tab.",
    });
  }

  private async buildTrashRecordingDiagnostics({
    audioRecordingId,
    modelData,
    context,
    associatedNoteId,
    isActiveRecording,
    pipelineSnapshot,
  }: {
    audioRecordingId: Uuid;
    modelData: AudioRecordingModelData;
    context: TrashRecordingContext;
    associatedNoteId: Uuid | null;
    isActiveRecording: boolean;
    pipelineSnapshot: ReturnType<AppStoreAudioPipelineStore["getSnapshot"]>;
  }): Promise<JsonObject> {
    const recordingStrategy = getAudioRecordingStrategy({ modelData });
    const recordingStrategyDriver = this.getStrategyDriver({ recordingStrategy });

    const [artifactSummary, uploadableArtifactStatus] = await Promise.all([
      recordingStrategyDriver.getArtifactDebugSummary({ audioRecordingId }),
      recordingStrategyDriver.getUploadableArtifactStatus({ audioRecordingId }),
    ]);

    return buildAudioRecordingArtifactDiagnostics({
      diagnosticsSource: "trash-recording",
      audioRecordingId,
      modelData,
      artifactAvailability: uploadableArtifactStatus.hasUploadableArtifact
        ? "uploadable"
        : artifactSummary.sizeBytes > 0
          ? "not-uploadable"
          : "empty-or-missing",
      artifactSizeBytes: artifactSummary.sizeBytes,
      artifactDurationMs: artifactSummary.durationMs,
      artifactPartCount: artifactSummary.partCount,
      hasUploadableArtifact: uploadableArtifactStatus.hasUploadableArtifact,
      extraInfo: {
        trashContext: context,
        associatedNoteId,
        isActiveRecording,
        pipelineStage: pipelineSnapshot?.stage ?? null,
        pipelineStageStatus: pipelineSnapshot?.stageStatus ?? null,
        pipelineTerminalOutcome: pipelineSnapshot?.terminalOutcome ?? null,
        pipelineDiagnosticKind: pipelineSnapshot?.diagnostic?.kind ?? null,
      },
    });
  }

  /**
   * Explicit source-upload trigger used by debug/admin flows and upload retry
   * affordances. This does not own orchestration; it only runs the upload
   * worker for the captured source artifact.
   */
  async startSourceUpload({
    audioRecordingId,
    forceRestart,
  }: {
    audioRecordingId: Uuid;
    forceRestart?: boolean;
  }): Promise<void> {
    await this.uploading.enqueue({ audioRecordingId, forceRestart });
  }

  /**
   * Canonical manual entrypoint for post-recording processing. Hands the
   * recording back to the pipeline, which decides whether it can use the local
   * transcript or must upload + retranscribe first.
   */
  async processRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    this.pipeline.startProcessing({ audioRecordingId });
  }

  private async deleteSourceForRecording({
    audioRecordingId,
    noteId,
  }: {
    audioRecordingId: Uuid;
    noteId: Uuid | null;
  }): Promise<void> {
    if (!noteId) return;

    try {
      const capturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
        associatedAudioRecordingId: audioRecordingId,
      });
      const sessionId = uuidModule.resolveCapturedAudioSessionSyncModelUuid({
        capturedAudioId,
      });
      const sessionArtifactId = uuidModule.resolveSessionArtifactIdForCapturedAudio({
        sessionId,
        capturedAudioId,
      });
      const sourceId = uuidModule.resolveSourceIdForSessionArtifact({
        sessionId,
        sessionArtifactId,
      });

      await deleteNoteSource({ store: this.store, noteId, sourceId });
      await new DeleteSourceOperation({
        store: this.store,
        payload: { source_id: sourceId },
      }).execute();
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioStore] Failed to delete audio source",
        info: {
          audioRecordingId,
          noteId,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  getUploadStateForRecording({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingUploadingState {
    return this.uploading.getProgress({ audioRecordingId });
  }

  getTransformStateForRecording({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingTransformingState {
    return this.transforming.getTransformState({ audioRecordingId });
  }

  private async withRecordingTabLock({
    audioRecordingId,
    action,
    toastMessage,
  }: RecordingLockAction): Promise<void> {
    try {
      await this.obtainAudioRecordingLock();
      await action();
    } catch (unknownErr) {
      await this.clearActiveAudioRecording({
        audioRecordingId,
      });
      await this.releaseAudioRecordingLock({ audioRecordingId });

      if (unknownErr instanceof AudioRecorderLockingError) {
        toastModule.triggerToast({
          content: toastMessage,
          toastId: audioRecordingId,
        });

        return;
      }

      logger.warn({
        message: "[AppStoreAudioStore] recording action failed",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(unknownErr as Error),
        },
      });

      throw unknownErr;
    }
  }

  private async setActiveAudioRecording({
    audioRecordingId,
    associatedNoteId,
  }: {
    audioRecordingId: Uuid;
    associatedNoteId?: Uuid | null;
  }): Promise<void> {
    await this.store.globalCache.set(CacheKey.ActiveAudioRecording, { audioRecordingId });
    runInAction(() => {
      this.activeRecordingIdValue = audioRecordingId;
      if (associatedNoteId !== undefined) {
        this.activeRecordingNoteIdValue = associatedNoteId;
      }
    });
  }

  private async clearActiveAudioRecording({
    audioRecordingId,
  }: {
    audioRecordingId?: Uuid | null;
  }): Promise<void> {
    /**
     * If a value is provided...
     * Then we perform a check to ensure that the value is the same as the active recording id.
     */
    if (audioRecordingId && this.activeRecordingIdValue) {
      if (audioRecordingId !== this.activeRecordingIdValue) {
        logger.info({
          message: "[AppStoreAudioStore] clearActiveAudioRecording: active recording id mismatch",
          info: {
            audioRecordingId,
            activeRecordingId: this.activeRecordingIdValue,
          },
        });

        return;
      }
    }

    const ownsLock = this.hasLockInCurrentTab;

    if (ownsLock) {
      await this.store.globalCache.del(CacheKey.ActiveAudioRecording);
    }
    runInAction(() => {
      this.activeRecordingIdValue = null;
      this.activeRecordingNoteIdValue = null;
    });
  }

  private async obtainAudioRecordingLock(): Promise<void> {
    if (this.activeRecordingLockHandle) {
      logger.info({
        message: "[AppStoreAudioStore] obtainAudioRecordingLock: already locked",
      });

      return;
    }

    const lockHandle = await audioRecordingModule.takeLock();
    runInAction(() => {
      this.activeRecordingLockHandle = lockHandle;
    });
  }

  private async maybeObtainAudioRecordingLock(): Promise<void> {
    try {
      await this.obtainAudioRecordingLock();
    } catch (unknownErr) {
      if (unknownErr instanceof AudioRecorderLockingError) {
        return;
      }

      logger.warn({
        message: "[AppStoreAudioStore] maybeObtainAudioRecordingLock: failed to obtain lock",
        info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
      });

      throw unknownErr;
    }
  }

  private async releaseAudioRecordingLock({
    audioRecordingId,
  }: {
    audioRecordingId?: Uuid | null;
  }): Promise<void> {
    /**
     * If a value is provided...
     * Then we perform a check to ensure that the value is the same as the active recording id.
     */
    if (audioRecordingId && this.activeRecordingIdValue) {
      if (audioRecordingId !== this.activeRecordingIdValue) {
        logger.info({
          message: "[AppStoreAudioStore] releaseAudioRecordingLock: active recording id mismatch",
          info: {
            audioRecordingId,
            activeRecordingId: this.activeRecordingIdValue,
          },
        });

        return;
      }
    }

    if (!this.activeRecordingLockHandle) {
      logger.info({
        message: "[AppStoreAudioStore] releaseAudioRecordingLock: no lock to release",
      });

      return;
    }

    await audioRecordingModule.releaseLock(this.activeRecordingLockHandle);
    runInAction(() => {
      this.activeRecordingLockHandle = null;
    });
  }

  async initialize(): Promise<void> {
    await this.rehydrateAndObtainAudioRecordingLockIfNecessary();
    const activeRecordingId = this.activeRecordingId;

    try {
      await this.recording.rehydrateActiveDesktopRecordingState({
        audioRecordingId: activeRecordingId,
      });
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioStore] active desktop recording recovery failed during initialize",
        info: {
          audioRecordingId: activeRecordingId,
          hadActiveRecordingLock: this.hasLockInCurrentTab,
          error: objectModule.safeErrorAsJson(error as Error),
        },
      });

      if (activeRecordingId) {
        await this.cleanupActiveDesktopRecordingAfterRecoveryFailure({
          audioRecordingId: activeRecordingId,
        });
      }
    }

    await this.reconcilePersistedAudioState();
  }

  private async cleanupActiveDesktopRecordingAfterRecoveryFailure({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<void> {
    try {
      await electronModule.recall.stopRecordingSession({ audioRecordingId });
    } catch (stopError) {
      logger.warn({
        message: "[AppStoreAudioStore] failed to stop active recording after recovery failure",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(stopError as Error),
        },
      });
    }

    try {
      await this.trashRecording({
        audioRecordingId,
        context: { reason: TrashRecordingReason.ActiveRecordingRecoveryFailed },
      });
      return;
    } catch (trashError) {
      logger.warn({
        message: "[AppStoreAudioStore] failed to trash active recording after recovery failure",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(trashError as Error),
        },
      });
    }

    try {
      await this.clearActiveAudioRecording({ audioRecordingId });
    } catch (clearError) {
      logger.warn({
        message: "[AppStoreAudioStore] failed to clear active recording after recovery failure",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(clearError as Error),
        },
      });
    }

    try {
      await this.releaseAudioRecordingLock({ audioRecordingId });
    } catch (releaseError) {
      logger.warn({
        message:
          "[AppStoreAudioStore] failed to release active recording lock after recovery failure",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(releaseError as Error),
        },
      });
    }
  }

  async rehydrateAndObtainAudioRecordingLockIfNecessary(): Promise<void> {
    let cachedData = await this.store.globalCache.get(CacheKey.ActiveAudioRecording);

    if (!cachedData) {
      // Try legacy cache for migration
      const legacyData = await LEGACY_GLOBAL_CACHE_INSTANCE.get(CacheKey.ActiveAudioRecording);
      if (legacyData) {
        await this.store.globalCache.set(CacheKey.ActiveAudioRecording, legacyData);

        // Clear legacy cache after migration
        await LEGACY_GLOBAL_CACHE_INSTANCE.del(CacheKey.ActiveAudioRecording);

        cachedData = legacyData;
      }
    }

    const cachedAudioRecordingId = cachedData?.audioRecordingId;
    if (!cachedAudioRecordingId) return;

    /**
     * We attempt to obtain the audio recording lock on-boot.
     *
     * The way it works is:
     * - Only one tab is allowed to access the "active recording" at a time.
     * - An active recording is defined as an audio-recording that is either recording or paused.
     * - The id is stored in the cache (dexie) and observed for changes.
     * - On boot, we start observing it.
     * - If it is in the active state (recording or paused) we obtain the lock.
     * - First tab to do this becomes the "primary" tab.
     */
    const isActivelyRecording =
      await this.store.dexie.repositories.audioRecordings.isInRecordingState({
        id: cachedAudioRecordingId,
      });

    if (!isActivelyRecording) {
      return;
    }

    try {
      await this.obtainAudioRecordingLock();
      await this.setActiveAudioRecording({ audioRecordingId: cachedAudioRecordingId });
    } catch (unknownErr) {
      if (unknownErr instanceof AudioRecorderLockingError) {
        return;
      }

      logger.warn({
        message:
          "[AppStoreAudioStore] rehydrateAndObtainAudioRecordingLockIfNecessary: failed to obtain lock",
        info: {
          error: objectModule.safeErrorAsJson(unknownErr as Error),
        },
      });

      throw unknownErr;
    }
  }

  private async reconcilePersistedAudioState(): Promise<void> {
    let shouldClearClientResetAudioRecoveryMarker = false;

    try {
      const { clientGroupId } = await clientEnvModule.clientGroupId();
      const recoveryPlan = await this.getClientResetAudioRecoveryPlan({
        currentClientGroupId: clientGroupId,
      });
      shouldClearClientResetAudioRecoveryMarker = recoveryPlan.shouldClearMarker;
      const records = await this.getPersistedAudioRecordsForReconciliation({
        currentClientGroupId: clientGroupId,
        recoveryContext: recoveryPlan.context,
      });

      for (const record of records) {
        const reconciledRecord = await this.recoverClientResetAudioRecordingIfNeeded({
          record,
          recoveryContext: recoveryPlan.context,
        });

        await this.reconcileRecordingState(reconciledRecord);
      }
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioStore] reconcilePersistedAudioState: failed",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
    } finally {
      if (shouldClearClientResetAudioRecoveryMarker) {
        await this.store.globalCache.del(CacheKey.ClientResetAudioRecovery);
      }
    }
  }

  /**
   * Builds the one-shot CLIENT_RESET audio recovery plan.
   *
   * The marker is written immediately before reset rotates the client group. A
   * broken client/server pair can force several reset/reload attempts before
   * audio initialization runs, so the marker may name multiple source groups. We
   * still consume it once: include only rows named by the marker, then delete it
   * in `finally` so old groups do not become permanent local aliases.
   */
  private async getClientResetAudioRecoveryPlan({
    currentClientGroupId,
  }: {
    currentClientGroupId: Uuid;
  }): Promise<ClientResetAudioRecoveryPlan> {
    const marker = await this.store.globalCache.get(CacheKey.ClientResetAudioRecovery);

    if (!marker) {
      return {
        context: null,
        shouldClearMarker: false,
      };
    }

    const previousClientGroupIds = marker.previousClientGroupIds.filter(
      previousClientGroupId => previousClientGroupId !== currentClientGroupId
    );

    if (previousClientGroupIds.length === 0) {
      return {
        context: null,
        shouldClearMarker: true,
      };
    }

    if (marker.audioRecordingIds.length === 0) {
      return {
        context: null,
        shouldClearMarker: true,
      };
    }

    return {
      context: {
        previousClientGroupIds: new Set(previousClientGroupIds as Uuid[]),
        currentClientGroupId,
        audioRecordingIds: new Set(marker.audioRecordingIds as Uuid[]),
      },
      shouldClearMarker: true,
    };
  }

  private async getPersistedAudioRecordsForReconciliation({
    currentClientGroupId,
    recoveryContext,
  }: {
    currentClientGroupId: Uuid;
    recoveryContext: ClientResetAudioRecoveryContext | null;
  }): Promise<IndexedAudioRecordingSyncUpdateValue[]> {
    const localTable = this.store.audioRecordings.localTable;
    const currentRecords: IndexedAudioRecordingSyncUpdateValue[] = await localTable
      .where("associated_client_id")
      .equals(currentClientGroupId)
      .filter(row => row.trashed_at === null)
      .toArray();

    if (!recoveryContext) {
      return currentRecords;
    }

    const previousClientGroupRecords = (
      await Promise.all(
        [...recoveryContext.previousClientGroupIds].map(previousClientGroupId => {
          return localTable
            .where("associated_client_id")
            .equals(previousClientGroupId)
            .filter(row => {
              return (
                row.trashed_at === null &&
                recoveryContext.audioRecordingIds.has(row.model_id as Uuid)
              );
            })
            .toArray();
        })
      )
    ).flat();

    const recordsById = new Map<Uuid, IndexedAudioRecordingSyncUpdateValue>();

    for (const record of currentRecords) {
      recordsById.set(record.model_id as Uuid, record);
    }

    for (const record of previousClientGroupRecords) {
      const audioRecordingId = record.model_id as Uuid;
      if (!recordsById.has(audioRecordingId)) {
        recordsById.set(audioRecordingId, record);
      }
    }

    return [...recordsById.values()];
  }

  private async recoverClientResetAudioRecordingIfNeeded({
    record,
    recoveryContext,
  }: {
    record: IndexedAudioRecordingSyncUpdateValue;
    recoveryContext: ClientResetAudioRecoveryContext | null;
  }): Promise<IndexedAudioRecordingSyncUpdateValue> {
    if (!recoveryContext) {
      return record;
    }

    if (!recoveryContext.previousClientGroupIds.has(record.associated_client_id as Uuid)) {
      return record;
    }

    const audioRecordingId = record.model_id as Uuid;
    if (!recoveryContext.audioRecordingIds.has(audioRecordingId)) {
      return record;
    }

    logger.info({
      message: "[AppStoreAudioStore] recovering audio recording after client reset",
      info: {
        audioRecordingId,
        previousClientGroupId: record.associated_client_id,
        currentClientGroupId: recoveryContext.currentClientGroupId,
      },
    });

    await this.store.dexie.repositories.audioRecordings.updateAssociatedClientId({
      id: audioRecordingId,
      associatedClientId: recoveryContext.currentClientGroupId,
    });

    return {
      ...record,
      associated_client_id: recoveryContext.currentClientGroupId,
      model_data: {
        ...record.model_data,
        associated_client_id: recoveryContext.currentClientGroupId,
      },
    };
  }

  private async reconcileRecordingState(
    record: IndexedAudioRecordingSyncUpdateValue
  ): Promise<void> {
    const audioRecordingId = record.model_id as Uuid;
    const modelData = record.model_data;

    if (modelData.trashed_at) {
      return;
    }

    if (modelData.recording_status !== AudioRecordingRecordingStatus.COMPLETED) {
      return;
    }

    if (!modelData.associated_note_id) {
      return;
    }

    const handoffSuccessorAudioRecordingId = getMeetingHandoffSuccessorAudioRecordingId({
      modelData,
    });
    if (isPipelineDeferredToMeetingHandoffSuccessor({ modelData })) {
      if (!this.hasTerminalUploadStatus({ modelData }) && handoffSuccessorAudioRecordingId) {
        const successorRecording = await this.store.dexie.repositories.audioRecordings.getById({
          id: handoffSuccessorAudioRecordingId,
        });

        if (successorRecording?.data.recording_status === AudioRecordingRecordingStatus.COMPLETED) {
          void this.uploading.enqueue({ audioRecordingId });
        }
      }

      return;
    }

    if (this.hasTerminalProcessingStatus({ modelData })) {
      if (!this.hasTerminalUploadStatus({ modelData })) {
        void this.uploading.enqueue({ audioRecordingId });
      }

      return;
    }

    if (this.hasUploadStatusThatBlocksProcessing({ modelData })) {
      return;
    }

    try {
      this.pipeline.startProcessing({ audioRecordingId });
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioStore] reconcileRecordingState: failed",
        info: {
          audioRecordingId,
          uploadStatus: modelData.upload_status,
          processingStatus: modelData.processing_status,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  private hasTerminalProcessingStatus({ modelData }: { modelData: AudioRecordingModelData }) {
    return (
      modelData.processing_status === AudioRecordingProcessingStatus.ERROR ||
      modelData.processing_status === AudioRecordingProcessingStatus.COMPLETED
    );
  }

  private hasTerminalUploadStatus({ modelData }: { modelData: AudioRecordingModelData }) {
    return (
      modelData.upload_status === AudioRecordingUploadStatus.COMPLETED ||
      modelData.upload_status === AudioRecordingUploadStatus.ERROR ||
      modelData.upload_status === AudioRecordingUploadStatus.SKIPPED
    );
  }

  private hasUploadStatusThatBlocksProcessing({
    modelData,
  }: {
    modelData: AudioRecordingModelData;
  }) {
    return (
      modelData.upload_status === AudioRecordingUploadStatus.ERROR ||
      modelData.upload_status === AudioRecordingUploadStatus.SKIPPED
    );
  }

  /**
   * Once the successor recording finishes, upload deferred predecessor source
   * artifacts in the background. The successor owns the visible combined
   * transcript transform, so predecessor segments should not enter the full
   * processing pipeline on their own.
   */
  private async startDeferredMeetingHandoffSegmentUploads({
    terminalAudioRecordingId,
  }: {
    terminalAudioRecordingId: Uuid;
  }): Promise<void> {
    const terminalRecording = await this.store.dexie.repositories.audioRecordings.getById({
      id: terminalAudioRecordingId,
    });
    const noteId = terminalRecording?.data.associated_note_id;
    if (!noteId) return;

    const deferredSegments = await this.store.audioRecordings.localTable
      .where("associated_note_id")
      .equals(noteId)
      .filter(row => row.trashed_at === null)
      .filter(
        row =>
          getMeetingHandoffSuccessorAudioRecordingId({ modelData: row.model_data }) ===
          terminalAudioRecordingId
      )
      .toArray();

    for (const segment of deferredSegments) {
      if (this.hasTerminalUploadStatus({ modelData: segment.model_data })) {
        continue;
      }

      void this.uploading.enqueue({ audioRecordingId: segment.model_id as Uuid });
    }
  }

  async dispose(): Promise<void> {
    this.clearDetectedMeetingHandoffTransition();
    await this.releaseAudioRecordingLock({ audioRecordingId: null });
  }

  async resetState(): Promise<void> {
    await this.maybeObtainAudioRecordingLock();

    this.ui.resetState();
    this.recording.resetState();
    this.uploading.resetState();
    this.transforming.resetState();
    this.pipeline.resetState();
    this.clearDetectedMeetingHandoffTransition();

    await this.clearActiveAudioRecording({ audioRecordingId: null });
    await this.releaseAudioRecordingLock({ audioRecordingId: null });
  }

  async ensureActiveRecordingStateIsValid(): Promise<void> {
    if (!this.activeRecordingId) return;

    /**
     * If we have an activeRecordingId, but no active recording on the device, we'll just
     * try resetting the state.
     *
     * The active recording should always be "in progress".
     * If it hasn't been started, or it has been completed, we'll clear it out.
     *
     * Note that - if a note doesn't have an `associatedNoteId`, this will also clear it out.
     * (This may become a valid state in the future, but for now it is always expected to be associated
     * with a note.)
     *
     * The recording should also always be from the current client device.
     */
    if (
      !this.activeRecording ||
      this.activeRecording.isTrashed ||
      !this.activeRecording.hasStartedRecording ||
      this.activeRecording.hasBeenCompleted ||
      !this.activeRecording.associatedNoteId ||
      !(await this.activeRecording.isFromCurrentClientDevice())
    ) {
      await this.resetState();
      return;
    }
  }

  async _activateRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    await this.setActiveAudioRecording({ audioRecordingId });
    await this.obtainAudioRecordingLock();
  }

  async _deactivateRecording(): Promise<void> {
    const activeRecordingId = this.activeRecordingIdValue;
    await this.clearActiveAudioRecording({ audioRecordingId: activeRecordingId });
    await this.releaseAudioRecordingLock({ audioRecordingId: activeRecordingId });
  }

  private get hasLockInCurrentTab(): boolean {
    return this.activeRecordingLockHandle !== null;
  }
}
